type Handlers = {
  play: () => void
  pause: () => void
  prev: () => void
  next: () => void
  seek: (time: number) => void
}

function ms(): MediaSession | null {
  if (typeof navigator === 'undefined') return null
  return ('mediaSession' in navigator) ? (navigator as Navigator).mediaSession : null
}

export function setMetadata(args: { title: string; artist: string; album: string }): void {
  const session = ms()
  if (!session) return
  if (typeof MediaMetadata === 'undefined') return
  session.metadata = new MediaMetadata({
    title: args.title,
    artist: args.artist,
    album: args.album,
  })
}

export function setActionHandlers(handlers: Handlers): void {
  const session = ms()
  if (!session) return
  try {
    session.setActionHandler('play', handlers.play)
    session.setActionHandler('pause', handlers.pause)
    session.setActionHandler('previoustrack', handlers.prev)
    session.setActionHandler('nexttrack', handlers.next)
    session.setActionHandler('seekto', (details) => {
      if (typeof details.seekTime === 'number') handlers.seek(details.seekTime)
    })
  } catch {
    // Older browsers may not support all action types; ignore.
  }
}

export function setPositionState(args: { duration: number; position: number; rate: number }): void {
  const session = ms()
  if (!session || typeof session.setPositionState !== 'function') return
  if (!isFinite(args.duration) || args.duration <= 0) return
  if (!isFinite(args.position) || args.position < 0) return
  try {
    session.setPositionState({
      duration: args.duration,
      position: Math.min(args.position, args.duration),
      playbackRate: args.rate,
    })
  } catch {
    // Some browsers throw on invalid state; safe to ignore.
  }
}

export function setPlaybackState(state: 'playing' | 'paused' | 'none'): void {
  const session = ms()
  if (!session) return
  session.playbackState = state
}

export function clearMediaSession(): void {
  const session = ms()
  if (!session) return
  session.metadata = null
  for (const action of ['play', 'pause', 'previoustrack', 'nexttrack', 'seekto'] as const) {
    try { session.setActionHandler(action, null) } catch { /* ignore */ }
  }
  session.playbackState = 'none'
}
